import { forwardRef, useEffect, useMemo, useRef, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import moment from "moment"
import toast from "react-hot-toast"
import { useReactToPrint } from "react-to-print"

import { Pagination } from "components"
import Table from "components/table"
import Modal from "components/modal"
import Input from "components/forms/input"
import Label from "components/forms/label"
import SelectDescription from "components/forms/select-description"
import { useAuth } from "contexts/auth-context"

import useStockTransferRecordRepositories from "repositories/stock-transfer-record"

import { getDefaultOutletFilterValue, resolveOutletFilter } from "helpers/general-helper"
import OutletFilterSelect from "components/forms/outlet-filter-select"

const STATUS_OPTIONS = [
  { id: "ALL", name: "Semua Status" },
  { id: "DRAFT", name: "Draft" },
  { id: "POSTED", name: "Posted" },
  { id: "CANCELLED", name: "Cancelled" },
  { id: "PARTIAL_RECEIVED", name: "Diterima Sebagian" },
  { id: "RECEIVED", name: "Received" },
  { id: "CLOSED", name: "Closed" },
]

const fmtPrintDate = (v) => {
  if (!v) return "-"
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return String(v)
  return new Intl.DateTimeFormat("id-ID", { day: "2-digit", month: "long", year: "numeric" }).format(d)
}

/**
 * 250726 (permintaan user): dokumen cetak Stock Transfer (client-side, react-to-print).
 * JUDUL DINAMIS tergantung outlet user yang mencetak:
 *  - outlet user = outlet TUJUAN  → "BARANG MASUK"
 *  - selain itu (outlet asal / lainnya) → "BARANG KELUAR"
 */
const StockTransferPrintDocument = forwardRef(function StockTransferPrintDocument({ transfer, currentOutletId }, ref) {
  const t = transfer || {}
  const isMasuk = String(currentOutletId ?? "") !== "" && String(currentOutletId ?? "") === String(t.dest_outlet_id ?? "")
  const title = isMasuk ? "BARANG MASUK" : "BARANG KELUAR"
  const items = Array.isArray(t.items) ? t.items : []
  const totalQty = items.reduce((sum, it) => sum + Number(it.qty_requested ?? 0), 0)
  const transferDate = t.transfer_date || t.display_date || t.created_at
  const printedAt = new Intl.DateTimeFormat("id-ID", {
    day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
  }).format(new Date())

  return (
    <div ref={ref} className="hidden print:block">
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { size: A4; margin: 12mm; }
        }
      `}</style>
      <div className="p-2 text-[12px] text-neutral-900">
        <div className="flex items-start justify-between pb-3 border-b border-neutral-300">
          <div>
            <div className="text-2xl font-bold tracking-wide">{title}</div>
            <div className="text-[12px] text-neutral-600">Bukti Transfer Stock</div>
          </div>
          <div className="text-right text-[12px]">
            <div>No. DO: <b>{t.delivery_order_no || "-"}</b></div>
            <div>Tanggal: {fmtPrintDate(transferDate)}</div>
            <div>Status: {t.status || "-"}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 py-3">
          <div>
            <div className="text-[11px] uppercase text-neutral-500">Dari Outlet (Pengirim)</div>
            <div className="font-medium">{t.source_outlet_name || "-"}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase text-neutral-500">Ke Outlet (Penerima)</div>
            <div className="font-medium">{t.dest_outlet_name || "-"}</div>
          </div>
        </div>

        <table className="w-full text-[12px] border-collapse">
          <thead>
            <tr className="bg-neutral-100">
              <th className="px-2 py-1 text-left border border-neutral-300 w-10">No</th>
              <th className="px-2 py-1 text-left border border-neutral-300">Produk</th>
              <th className="px-2 py-1 text-right border border-neutral-300 w-24">Qty</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={3} className="px-2 py-3 text-center border border-neutral-300 text-neutral-500">Tidak ada item</td></tr>
            )}
            {items.map((it, i) => (
              <tr key={it.id ?? i}>
                <td className="px-2 py-1 align-top border border-neutral-300">{i + 1}</td>
                <td className="px-2 py-1 align-top border border-neutral-300">{it.product_unit_name || it.product_unit_id || "-"}</td>
                <td className="px-2 py-1 text-right align-top border border-neutral-300">{Number(it.qty_requested ?? 0)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="font-semibold bg-neutral-50">
              <td colSpan={2} className="px-2 py-1 text-right border border-neutral-300">Total Qty</td>
              <td className="px-2 py-1 text-right border border-neutral-300">{totalQty}</td>
            </tr>
          </tfoot>
        </table>

        <div className="grid grid-cols-2 gap-10 mt-16 text-center text-[12px]">
          <div><div className="pt-1 mx-6 border-t border-neutral-400">Pengirim</div></div>
          <div><div className="pt-1 mx-6 border-t border-neutral-400">Penerima</div></div>
        </div>

        <div className="mt-6 text-[10px] text-neutral-500">
          {t.delivery_order_no ? `${t.delivery_order_no} • ` : ""}Dicetak pada {printedAt}
        </div>
      </div>
    </div>
  )
})

export default function List() {
  const { can, isSuper, currentBranch, canFilterAllOutlets } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const searchInput = useRef(null)
  const [searchText, setSearchText] = useState(searchParams.get("search") || "")
  const [detailTransfer, setDetailTransfer] = useState(null)

  // 250726 (permintaan user): print Stock Transfer (client-side). Judul dinamis di
  // dokumen (BARANG MASUK bila outlet user = tujuan, else BARANG KELUAR).
  const printRef = useRef()
  const [printTarget, setPrintTarget] = useState(null)
  const handlePrint = useReactToPrint({
    documentTitle: `Transfer ${printTarget?.transfer?.delivery_order_no || ""}`.trim(),
    removeAfterPrint: true,
  })
  const onPrint = (v) => setPrintTarget({ transfer: v, nonce: Date.now() })
  useEffect(() => {
    if (printTarget?.transfer) handlePrint(null, () => printRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printTarget])

  const isBranchLocked = !canFilterAllOutlets() && !!currentBranch?.id // ATALLA-403: pemegang permission filter-entity bebas pilih outlet entitasnya
  // Nilai filter outlet: sentinel grup (GRP_ALL/GRP_HO) atau id outlet tunggal.
  // Default "ikut outlet user"; branch-locked dipaksa ke outlet sendiri.
  const rawOutletId = searchParams.get("outlet_id") ?? getDefaultOutletFilterValue()
  const outletFilter = isBranchLocked ? String(currentBranch.id) : rawOutletId
  // Grup/outlet → CSV id utk kontrak endpoint outlet_id (single/CSV).
  const { outlet_id_csv: resolvedOutletCsv } = resolveOutletFilter(outletFilter)
  const status = searchParams.get("status") || "ALL"
  const dateFrom = searchParams.get("date_from") || ""
  const dateTo = searchParams.get("date_to") || ""
  const page = searchParams.get("page") || "1"

  const { data, isLoading, mutate } = useStockTransferRecordRepositories({
    search: searchParams.get("search") || undefined,
    outlet_id: resolvedOutletCsv || undefined,
    status: status === "ALL" ? undefined : status,
    with_items: 1,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    page,
  })

  const rows = useMemo(() => data?.data ?? [], [data])
  const canCreateTransfer = can("stock-transfer.create") || can("stock-movement.view")

  const mergeParams = (next) => {
    const current = Object.fromEntries(searchParams.entries())
    const merged = { ...current, ...next }

    Object.keys(merged).forEach((key) => {
      if (merged[key] === null || merged[key] === undefined || merged[key] === "") {
        delete merged[key]
      }
    })

    setSearchParams(merged)
  }

  const submitSearch = () => {
    mergeParams({ search: searchText || null, page: 1 })
  }

  const headers = [
    { isAction: false, content: "No. DO" },
    { isAction: false, content: "Tanggal" },
    { isAction: false, content: "Item" },
    { isAction: false, content: "Dari Outlet" },
    { isAction: false, content: "Ke Outlet" },
    { isAction: false, content: "Status" },
    { isAction: false, content: "Qty" },
    { isAction: false, content: "Dibuat Oleh" },
    { isAction: true, content: "Aksi" },
  ]

  const postTransfer = async (row) => {
    const confirmed = window.confirm(`Post Stock Transfer ${row.delivery_order_no}?`)
    if (!confirmed) return

    try {
      const response = await fetch(
        `${process.env.REACT_APP_INVENTORY_API_URL}/stock-transfers/${row.id}/post`,
        {
          method: "POST",
          headers: { Accept: "application/json" },
        }
      )
      const json = await response.json()
      if (!response.ok || Number(json?.success) !== 1) {
        throw new Error(json?.message || "Gagal post stock transfer")
      }

      toast.success(json?.message || "Stock transfer berhasil diposting")
      await mutate()
    } catch (error) {
      toast.error(error?.message || "Gagal post stock transfer")
    }
  }

  const cancelTransfer = async (row) => {
    const confirmed = window.confirm(`Batalkan draft stock transfer ${row.delivery_order_no}?`)
    if (!confirmed) return

    try {
      const response = await fetch(
        `${process.env.REACT_APP_INVENTORY_API_URL}/stock-transfers/${row.id}/cancel`,
        {
          method: "POST",
          headers: { Accept: "application/json" },
        }
      )
      const json = await response.json()
      if (!response.ok || Number(json?.success) !== 1) {
        throw new Error(json?.message || "Gagal cancel stock transfer")
      }

      toast.success(json?.message || "Draft stock transfer berhasil dibatalkan")
      await mutate()
    } catch (error) {
      toast.error(error?.message || "Gagal cancel stock transfer")
    }
  }

  // 280726 (permintaan user): CLOSE transfer — sisa yang belum diterima
  // dikembalikan ke outlet asal, dokumen selesai.
  const closeTransfer = async (row) => {
    const sisa = Number(row.total_qty_requested ?? 0) - Number(row.total_qty_received ?? 0)
    const confirmed = window.confirm(
      `Close transfer ${row.delivery_order_no}?\n\n` +
        (sisa > 0
          ? `Sisa ${sisa} pcs yang belum diterima akan DIKEMBALIKAN ke outlet asal.\n`
          : "") +
        "Setelah di-close, transfer tidak bisa diterima lagi."
    )
    if (!confirmed) return

    try {
      const response = await fetch(
        `${process.env.REACT_APP_INVENTORY_API_URL}/stock-transfers/${row.id}/close`,
        { method: "POST", headers: { Accept: "application/json" } }
      )
      const json = await response.json()
      if (!response.ok || Number(json?.success) !== 1) {
        throw new Error(json?.message || "Gagal close stock transfer")
      }

      toast.success(json?.message || "Transfer di-close")
      await mutate()
    } catch (error) {
      toast.error(error?.message || "Gagal close stock transfer")
    }
  }

  const columns = [
    {
      type: "default",
      bold: true,
      content: (v) => (
        <div className="flex flex-col gap-1">
          <span>{v.delivery_order_no || "-"}</span>
          {Number(v._movement_count ?? 0) > 0 && (
            <span className="text-[11px] font-medium text-blue-600">
              {v._movement_count} baris pergerakan stok
            </span>
          )}
        </div>
      ),
    },
    { type: "default", content: (v) => (v.display_date || v.transfer_date || v.created_at) ? moment(v.display_date || v.transfer_date || v.created_at).format("YYYY-MM-DD HH:mm") : "-" },
    {
      type: "default",
      content: (v) => (
        <div className="flex items-center gap-2">
          <span className="text-sm text-neutral-700">{v.total_items || 0} item</span>
          <button
            type="button"
            onClick={() => setDetailTransfer(v)}
            className="px-2 py-1 text-[11px] font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md"
          >
            Detail
          </button>
        </div>
      ),
    },
    { type: "default", content: (v) => v.source_outlet_name || "-" },
    { type: "default", content: (v) => v.dest_outlet_name || "-" },
    {
      type: "default",
      content: (v) => (
        <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border ${
          v.status === "DRAFT"
            ? "bg-amber-50 text-amber-700 border-amber-200"
            : v.status === "POSTED"
            ? "bg-blue-50 text-blue-700 border-blue-200"
            : v.status === "CANCELLED"
            ? "bg-red-50 text-red-700 border-red-200"
            : v.status === "PARTIAL_RECEIVED"
            ? "bg-orange-50 text-orange-700 border-orange-200"
            : v.status === "CLOSED"
            ? "bg-neutral-100 text-neutral-600 border-neutral-300"
            : "bg-green-50 text-green-700 border-green-200"
        }`}>
          {v.status}
        </span>
      ),
    },
    {
      type: "default",
      content: (v) => (
        <div className="text-right">
          <div className="font-semibold text-neutral-700">{v.total_qty_requested ?? v.qty ?? 0}</div>
          {Number(v.total_qty_received ?? 0) > 0 && (
            <div className="text-[11px] text-neutral-500">diterima {v.total_qty_received}</div>
          )}
        </div>
      ),
    },
    { type: "default", content: (v) => v.created_by || "-" },
    {
      type: "action",
      content: (v) => (
        <div className="flex items-center gap-2">
          {v.status === "DRAFT" && (
            <>
              <Link
                to={`/stock-transfer/${v.id}/edit`}
                className="px-3 py-1.5 text-xs font-medium text-sky-700 bg-sky-50 border border-sky-200 rounded-lg"
              >
                Lanjutkan
              </Link>
              <button
                type="button"
                onClick={() => postTransfer(v)}
                className="px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg"
              >
                Posting
              </button>
              <button
                type="button"
                onClick={() => cancelTransfer(v)}
                className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg"
              >
                Batalkan
              </button>
            </>
          )}
          {(v.status === "POSTED" || v.status === "PARTIAL_RECEIVED") && (
            <button
              type="button"
              onClick={() => closeTransfer(v)}
              className="px-3 py-1.5 text-xs font-medium text-neutral-700 bg-neutral-50 border border-neutral-300 rounded-lg hover:bg-neutral-100"
              title="Sisa yang belum diterima dikembalikan ke outlet asal"
            >
              Close
            </button>
          )}
          {/* 250726 (permintaan user): tombol print — judul dokumen dinamis. */}
          <button
            type="button"
            onClick={() => onPrint(v)}
            className="px-3 py-1.5 text-xs font-medium text-neutral-700 bg-neutral-100 border border-neutral-200 rounded-lg hover:bg-neutral-200"
          >
            Print
          </button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="p-5 bg-white border rounded-2xl border-neutral-200">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">Stock Transfer</h2>
            <p className="mt-1 text-sm text-neutral-500">
              Daftar transfer stock antar outlet berdasarkan nomor Delivery Order.
            </p>
          </div>

          {canCreateTransfer && (
            <Link
              to="/stock-transfer/create"
              className="inline-flex items-center px-4 py-3 text-sm font-medium text-white bg-brand-900 rounded-xl"
            >
              Buat Stock Transfer
            </Link>
          )}
        </div>

          <div className="grid grid-cols-1 gap-3 mt-5 md:grid-cols-5">
          <div>
            <Label value="Outlet" />
            <OutletFilterSelect
              value={outletFilter}
              onChange={(v) => mergeParams({ outlet_id: v || null, page: 1 })}
              disabled={isBranchLocked}
              className="block w-full px-2 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-50 disabled:opacity-75"
              title="Outlet"
            />
          </div>
          <div>
            <Label value="Status" />
            <SelectDescription
              selection={STATUS_OPTIONS}
              value={status}
              onChange={(value) => mergeParams({ status: value || "ALL", page: 1 })}
              keyValue={(v) => v.id}
              title={(v) => v.name}
              description={() => ""}
              placeholder="Pilih Status..."
            />
          </div>
          <div>
            <Label value="Tanggal Dari" />
            <Input type="date" value={dateFrom} onChange={(e) => mergeParams({ date_from: e.target.value, page: 1 })} />
          </div>
          <div>
            <Label value="Tanggal Sampai" />
            <Input type="date" value={dateTo} onChange={(e) => mergeParams({ date_to: e.target.value, page: 1 })} />
          </div>
          <div>
            <Label value="Cari Delivery Order" />
            <Input
              ref={searchInput}
              type="text"
              value={searchText}
              placeholder="No. delivery order..."
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitSearch() } }}
            />
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-2xl border-neutral-200">
        <Table
          isLoading={isLoading}
          data={rows}
          headers={headers}
          columns={columns}
          key={(v) => `${v.delivery_order_no}-${v.product_unit_id}`}
          isEmpty={!rows.length}
        />
        <Pagination links={data?.links} from={data?.from} to={data?.to} total={data?.total} />
      </div>

      <Modal
        isOpen={!!detailTransfer}
        onClose={() => setDetailTransfer(null)}
        title={`Detail Transfer ${detailTransfer?.delivery_order_no || ""}`}
        size="3xl"
        content={(
          <div className="mt-6 space-y-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <div className="p-3 border rounded-xl border-neutral-200">
                <div className="text-[11px] uppercase text-neutral-500">Dari Outlet</div>
                <div className="mt-1 text-sm font-medium text-neutral-900">{detailTransfer?.source_outlet_name || "-"}</div>
              </div>
              <div className="p-3 border rounded-xl border-neutral-200">
                <div className="text-[11px] uppercase text-neutral-500">Outlet Penerima</div>
                <div className="mt-1 text-sm font-medium text-neutral-900">{detailTransfer?.dest_outlet_name || "-"}</div>
              </div>
              <div className="p-3 border rounded-xl border-neutral-200">
                <div className="text-[11px] uppercase text-neutral-500">Status</div>
                <div className="mt-1 text-sm font-medium text-neutral-900">{detailTransfer?.status || "-"}</div>
              </div>
              <div className="p-3 border rounded-xl border-neutral-200">
                <div className="text-[11px] uppercase text-neutral-500">Total Qty</div>
                <div className="mt-1 text-sm font-medium text-neutral-900">{detailTransfer?.total_qty_requested ?? detailTransfer?.qty ?? 0}</div>
              </div>
            </div>

            <div className="overflow-hidden border rounded-xl border-neutral-200">
              <table className="min-w-full text-sm">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Produk</th>
                    <th className="px-3 py-2 text-right">Qty Transfer</th>
                  </tr>
                </thead>
                <tbody>
                  {!(detailTransfer?.items || []).length && (
                    <tr>
                      <td colSpan={2} className="px-3 py-4 text-center text-neutral-500">
                        Detail item belum tersedia.
                      </td>
                    </tr>
                  )}
                  {(detailTransfer?.items || []).map((item) => (
                    <tr key={item.id} className="border-t hover:bg-brand-50 transition-colors">
                      <td className="px-3 py-3">{item.product_unit_name || "-"}</td>
                      <td className="px-3 py-3 text-right">{item.qty_requested ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      />

      {/* 250726: dokumen cetak tersembunyi (react-to-print) — judul dinamis per outlet user. */}
      <StockTransferPrintDocument
        ref={printRef}
        transfer={printTarget?.transfer}
        currentOutletId={currentBranch?.id}
      />
    </div>
  )
}
